import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import {
  Plus, MapPin, Clock, Calendar, Trash2, GripVertical,
  CheckCircle, Circle, ChevronDown, ChevronUp, Lock, Sparkles
} from "lucide-react";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

export const ItineraryPanel = ({ bucketItemId, bucketItemTitle, isOpen, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isPremiumLocked, setIsPremiumLocked] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    time: "",
    location: "",
    notes: ""
  });
  
  const { hasPaidAccess } = useEntitlements();

  const fetchItinerary = async () => {
    try {
      const res = await axios.get(`${API}/items/${bucketItemId}/itinerary`);
      setItems(res.data);
      setIsPremiumLocked(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsPremiumLocked(true);
      } else {
        console.error("Failed to fetch itinerary");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && bucketItemId) {
      fetchItinerary();
    }
  }, [isOpen, bucketItemId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    try {
      await axios.post(`${API}/items/${bucketItemId}/itinerary`, {
        ...formData,
        order: items.length
      });
      toast.success("Activity added!");
      setFormData({ title: "", date: "", time: "", location: "", notes: "" });
      setShowAddForm(false);
      fetchItinerary();
    } catch (error) {
      toast.error("Failed to add activity");
    }
  };

  const handleToggleComplete = async (item) => {
    try {
      await axios.put(`${API}/itinerary/${item.id}`, {
        completed: !item.completed
      });
      fetchItinerary();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (item) => {
    try {
      await axios.delete(`${API}/itinerary/${item.id}`);
      toast.success("Activity removed");
      fetchItinerary();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-none rounded-2xl p-0 max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
          <DialogTitle className="font-serif text-xl text-charcoal">
            Plan Your Adventure
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{bucketItemTitle}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : isPremiumLocked ? (
            /* Premium Locked State */
            <div className="relative">
              <div className="blur-sm select-none pointer-events-none">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-background rounded-xl p-4 border border-charcoal/5 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-muted" />
                      <div className="flex-1">
                        <div className="h-4 w-3/4 bg-muted rounded mb-2" />
                        <div className="h-3 w-1/2 bg-muted rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-card via-card/95 to-card/80 rounded-xl">
                <div className="text-center p-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center">
                    <Calendar className="w-8 h-8 text-blue-500" />
                  </div>
                  
                  <h3 className="font-serif text-lg font-semibold mb-2">
                    AI Trip Planner
                  </h3>
                  
                  <p className="text-sm text-muted-foreground mb-4 max-w-[250px]">
                    Create detailed itineraries with AI-powered suggestions
                  </p>
                  
                  <Button
                    onClick={() => setShowPaywall(true)}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:opacity-90 rounded-full px-6"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Unlock Planner
                  </Button>
                </div>
              </div>
            </div>
          ) : items.length === 0 && !showAddForm ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-medium text-charcoal mb-2">No activities yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add activities, locations, and times to plan this adventure
              </p>
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-primary text-white hover:bg-primary/90 rounded-full btn-press"
                data-testid="add-first-activity-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Activity
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Activity List */}
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className={`bg-background rounded-xl p-4 border border-charcoal/5 ${
                    item.completed ? "opacity-60" : ""
                  }`}
                  data-testid={`itinerary-item-${item.id}`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => handleToggleComplete(item)}
                      className="mt-0.5 flex-shrink-0"
                      data-testid={`toggle-itinerary-${item.id}`}
                    >
                      {item.completed ? (
                        <CheckCircle className="w-5 h-5 text-secondary" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground hover:text-primary" />
                      )}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium text-charcoal ${item.completed ? "line-through" : ""}`}>
                        {item.title}
                      </h4>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                        {item.date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {format(new Date(item.date), "MMM d")}
                          </span>
                        )}
                        {item.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {item.time}
                          </span>
                        )}
                        {item.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {item.location}
                          </span>
                        )}
                      </div>
                      
                      {item.notes && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {item.notes}
                        </p>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(item)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      data-testid={`delete-itinerary-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Add Form */}
              {showAddForm ? (
                <form onSubmit={handleSubmit} className="bg-background rounded-xl p-4 border-2 border-dashed border-primary/30">
                  <div className="space-y-4">
                    <Input
                      placeholder="Activity name *"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-card border-charcoal/10 rounded-lg"
                      data-testid="itinerary-title-input"
                    />
                    
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="bg-card border-charcoal/10 rounded-lg"
                        data-testid="itinerary-date-input"
                      />
                      <Input
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="bg-card border-charcoal/10 rounded-lg"
                        data-testid="itinerary-time-input"
                      />
                    </div>
                    
                    <Input
                      placeholder="Location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="bg-card border-charcoal/10 rounded-lg"
                      data-testid="itinerary-location-input"
                    />
                    
                    <Textarea
                      placeholder="Notes (optional)"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-card border-charcoal/10 rounded-lg resize-none"
                      rows={2}
                      data-testid="itinerary-notes-input"
                    />
                    
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowAddForm(false);
                          setFormData({ title: "", date: "", time: "", location: "", notes: "" });
                        }}
                        className="flex-1 rounded-lg border-charcoal/10"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-lg"
                        data-testid="save-itinerary-btn"
                      >
                        Add Activity
                      </Button>
                    </div>
                  </div>
                </form>
              ) : (
                <Button
                  onClick={() => setShowAddForm(true)}
                  variant="outline"
                  className="w-full h-12 border-dashed border-charcoal/20 text-muted-foreground hover:text-primary hover:border-primary/30 rounded-xl"
                  data-testid="add-activity-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Activity
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      
      {/* Premium Paywall */}
      <PaywallScreen
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        type={PaywallType.WORLD_MAP}
        triggerSource="itinerary_planner"
      />
    </Dialog>
  );
};

export default ItineraryPanel;
